/*
 * Project: BookingEngine
 * Module: components/booking/rooms/RoomDetail.js
 * vim:ft=javascript.jsx:
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import DoubleSlider from './DoubleSlider';
import Quota from './Quota';
import RoomHighlights from './RoomHighlights';
import CloseIcon from '../../icons/CloseIcon';
import IframeVideo360 from './IframeVideo360';
import { PALAMIA_CHAIN } from '../../../constants';

import { getKeySellingPoints } from './RoomKeySellingPoints';

import { getLangValue } from '../../../utils/Generic';

import '../../../static/less/rooms/room_detail.less';
import { DEVICES } from '../../../actions/Booking';

class RoomDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = { photosWidth: null, photosHeight: null };
    this.closeRoomPopUp = this.closeRoomPopUp.bind(this);
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillMount() {
    document.body.classList.add('lock-scroll');
  }

  componentWillUnmount() {
    document.body.classList.remove('lock-scroll');
  }

  closeRoomPopUp() {
    document.body.classList.remove('lock-scroll');
    this.props.onCloseRoomDetails();
  }

  filterAmenities(amenities) {
    const freeAmenities = [];
    const extraAmenities = [];
    amenities.forEach((amenity) => {
      if (amenity.extra_cost) {
        extraAmenities.push(amenity);
      } else {
        freeAmenities.push(amenity);
      }
    });
    return { freeAmenities, extraAmenities };
  }

  renderRoomCategories(room) {
    return (
      <div className="categories">
        <div className="category-name">{room.adults_only ? this.context.t('Adults Only') : ''}</div>
      </div>
    );
  }

  renderListOfAmenities(elements, title, style) {
    const langValue = elements[0].name ? 'name' : 'text';
    return (
      <div className="amenity-block">
        <h3 className="amenity-title">{title}:</h3>
        <ul className="amenity-list">
          {elements.map((item, index) => (
            <li className={style} key={`amenity_${index}`}>
              {getLangValue(item, this.props.lang, langValue).toLowerCase()}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderAmenities(amenities) {
    if (amenities.length === 0) {
      return null;
    }
    // Get Amenities.
    const { freeAmenities, extraAmenities } = this.filterAmenities(amenities);
    // Get Selling Points.
    const sellingPoints = getKeySellingPoints([this.props.room]);
    // Depending on which chain is, we render a different amenities title.
    const titles = {
      included: this.context.t('What is included'),
      extraCost: this.context.t('Extra cost'),
    };
    if (this.props.chain === PALAMIA_CHAIN) {
      titles.included = this.context.t('Suite Inclusions');
    }
    // Return
    return (
      <div className="amenities">
        {freeAmenities.length > 0 && this.renderListOfAmenities(freeAmenities, titles.included, 'included')}
        {extraAmenities.length > 0 && this.renderListOfAmenities(extraAmenities, titles.extraCost, 'extra-cost')}
        {sellingPoints && this.renderListOfAmenities(sellingPoints, this.context.t('Key selling points'), 'selling-point') }
      </div>
    );
  }

  renderHightlights(highlights) {
    return highlights.length > 0 && <RoomHighlights highlights={highlights} lang={this.props.lang} />;
  }

  renderRoomHeader(room) {
    return (
      <div className={`RoomDetailHeader${room.quota > 9 ? '' : ' RoomDetailHeader--quota'}`}>
        <h2 className="RoomDetailHeader__title">
          {getLangValue(room, this.props.lang, 'name').toLocaleLowerCase()}
        </h2>
        <Quota quota={room.quota} chain={this.props.chain} />
      </div>
    );
  }

  renderRoomInfo(room) {
    return (
      <div className="RoomDetailInfo">
        <p className="mt-0">{getLangValue(room, this.props.lang, 'description')}</p>
        <div className="more-details">
          {this.renderRoomCategories(room)}
        </div>
      </div>
    );
  }

  showOptions(list) {
    const { format } = this.props;
    return list.includes(format);
  }

  showGallery() {
    const galleryOptions = ['full', 'gallery'];
    return this.showOptions(galleryOptions);
  }

  showDescription() {
    const galleryOptions = ['full', 'description'];
    return this.showOptions(galleryOptions);
  }

  show360() {
    const options = ['full', 'room-360'];
    return this.showOptions(options);
  }

  showFloorPlan() {
    const options = ['full', 'floor-plan'];
    return this.showOptions(options);
  }

  showVideo() {
    const options = ['full', 'room-video'];
    return this.showOptions(options);
  }

  render() {
    const { room, isMobile, children } = this.props;

    if (this.showGallery()) {
      return (
        <DoubleSlider
          photos={room.photos}
          imageSize={isMobile ? 'sm' : 'xl'}
          onClose={this.closeRoomPopUp}
          room={this.props.room}
          roomName={getLangValue(room, this.props.lang, 'name')}
        />
      );
    }

    return (
      <div className="RoomDetail">
        <div className="RoomDetailOverlay" onClick={this.closeRoomPopUp} />
        <div className="RoomDetailContent container">
          <CloseIcon onClick={this.closeRoomPopUp} className="closeIcon" />
          <div className="RoomDetailContent-wrapper">
            {children}
            {this.showDescription() && <div className="room-detail__description">
              {this.renderRoomHeader(room)}
              {this.renderRoomInfo(room)}
              {this.renderHightlights(room.highlights)}
              {this.renderAmenities(room.amenities)}
            </div> }
            {this.show360() && <div className="room-detail__description video_360">
              <IframeVideo360 src={room.url_360} />
            </div>}
            {this.showFloorPlan() && <div className="room-detail__description floor_plan">
                <img src={room.floor_plan} />
            </div>}
            {this.showVideo() && <div className="room-detail__description video_360">
              <IframeVideo360 src={room.url_youtube} />
            </div>}
          </div>
        </div>
      </div>
    );
  }
}

RoomDetail.propTypes = {
  room: PropTypes.object.isRequired,
  format: PropTypes.string,
};

RoomDetail.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default connect((state) => ({
  chain: state.bookingFormState.chain,
  isMobile: state.booking.device === DEVICES.MOBILE,
  lang: state.i18nState.lang,
}))(RoomDetail);
